import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, formatDateTimeIT } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import {
  Ticket, Phone, Envelope, House, Wrench, Clock, Trash, CheckCircle, PencilSimple, PaperPlaneTilt, UserPlus, ArrowClockwise, FloppyDisk, Eye, EyeSlash, Warning,
} from "@phosphor-icons/react";

const DEVICE_LABELS = {
  server: "Server", notebook: "Notebook", desktop: "PC Desktop", stampante: "Stampante",
  rete: "Rete/Wi-Fi", nas: "NAS", software: "Software", altro: "Altro",
};

const URGENCY_META = {
  low: { l: "Bassa", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30" },
  normal: { l: "Normale", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30" },
  high: { l: "Alta", cls: "bg-rose-500/15 text-rose-700 dark:text-rose-400 border-rose-500/30" },
};

const STATUS_META = {
  open: { l: "Aperto", cls: "bg-primary/15 text-primary border-primary/30" },
  closed: { l: "Chiuso", cls: "bg-muted text-muted-foreground border-border" },
};

export default function TicketsPage() {
  useDocumentTitle("Ticket assistenza");
  const [items, setItems] = useState([]);
  const [showClosed, setShowClosed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, ticket: null, form: {} });
  const [closeDialog, setCloseDialog] = useState({ open: false, ticket: null, step: 1, addToMovements: null, price: "", note: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [savingClose, setSavingClose] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tickets");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 45s
  useEffect(() => {
    const t = setInterval(() => { load(); }, 45000);
    return () => clearInterval(t);
  }, [load]);

  const openTickets = items.filter((t) => t.status === "open");
  const closedTickets = items.filter((t) => t.status === "closed");
  const visible = showClosed ? closedTickets : openTickets;

  const setUrgency = async (ticket, u) => {
    try {
      const { data } = await api.patch(`/tickets/${ticket.id}`, { urgency: u });
      setItems((prev) => prev.map((x) => x.id === ticket.id ? data : x));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const reopenTicket = async (ticket) => {
    if (!window.confirm("Riaprire questo ticket chiuso?")) return;
    try {
      const { data } = await api.patch(`/tickets/${ticket.id}`, { status: "open" });
      setItems((prev) => prev.map((x) => x.id === ticket.id ? data : x));
      toast.success("Ticket riaperto");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const removeTicket = async (t) => {
    if (!window.confirm(`Eliminare definitivamente il ticket di ${t.surname} ${t.name}?`)) return;
    try {
      await api.delete(`/tickets/${t.id}`);
      setItems((prev) => prev.filter((x) => x.id !== t.id));
      toast.success("Ticket eliminato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const createCustomer = async (t) => {
    try {
      const { data } = await api.post(`/tickets/${t.id}/create-customer`);
      toast.success(data.was_existing ? "Cliente esistente collegato" : "Cliente creato e collegato");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const composeEmail = (t) => {
    if (!t.email) { toast.error("Nessuna email registrata"); return; }
    const subject = `Diotech — Richiesta assistenza (${t.surname} ${t.name})`;
    const body = `Gentile ${t.surname},\n\nla contattiamo in merito alla sua richiesta di assistenza:\n\n"${t.description}"\n\nRestiamo a disposizione per qualsiasi chiarimento.`;
    window.location.href = `mailto:${encodeURIComponent(t.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const openEdit = (t) => {
    setEditDialog({
      open: true, ticket: t,
      form: {
        name: t.name || "", surname: t.surname || "", company_name: t.company_name || "", phone: t.phone || "", email: t.email || "",
        contact_type: t.contact_type, device_type: t.device_type, description: t.description || "",
        urgency: t.urgency || "normal", notes: t.notes || "",
      },
    });
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/tickets/${editDialog.ticket.id}`, editDialog.form);
      setItems((prev) => prev.map((x) => x.id === data.id ? data : x));
      setEditDialog({ open: false, ticket: null, form: {} });
      toast.success("Ticket aggiornato");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingEdit(false); }
  };

  const startClose = (t) => setCloseDialog({ open: true, ticket: t, step: 1, addToMovements: null, price: "", note: "" });

  const submitClose = async () => {
    const t = closeDialog.ticket;
    const addMove = !!closeDialog.addToMovements;
    if (addMove) {
      const p = parseFloat(closeDialog.price);
      if (!p || p <= 0) { toast.error("Inserisci un prezzo valido"); return; }
      if (!t.customer_id) { toast.error("Devi prima creare/collegare il cliente al ticket"); return; }
    }
    setSavingClose(true);
    try {
      const payload = {
        add_to_movements: addMove,
        price: addMove ? parseFloat(closeDialog.price) : null,
        customer_id: t.customer_id || null,
        note: closeDialog.note || null,
      };
      const { data } = await api.post(`/tickets/${t.id}/close`, payload);
      setItems((prev) => prev.map((x) => x.id === data.id ? data : x));
      setCloseDialog({ open: false, ticket: null, step: 1, addToMovements: null, price: "", note: "" });
      toast.success(addMove ? "Ticket chiuso e movimento aggiunto" : "Ticket chiuso");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSavingClose(false); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="tickets-page">
      <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-2">// RICHIESTE ESTERNE</div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold tracking-tight flex items-center gap-3">
            <Ticket size={36} weight="duotone" className="text-primary" />
            Ticket assistenza
          </h1>
          <p className="text-muted-foreground mt-2">
            Richieste dal form pubblico <a href="/richiesta-assistenza" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline font-mono">/richiesta-assistenza</a> — aggiornamento automatico ogni 45s.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={showClosed ? "default" : "outline"}
            onClick={() => setShowClosed(!showClosed)}
            data-testid="toggle-closed-btn"
            className="rounded-full gap-2"
          >
            {showClosed ? <EyeSlash size={14} /> : <Eye size={14} />}
            {showClosed ? `Nascondi chiusi (${closedTickets.length})` : `Mostra chiusi (${closedTickets.length})`}
          </Button>
          <Button variant="outline" onClick={load} data-testid="tickets-refresh-btn" className="rounded-full gap-2">
            <ArrowClockwise size={14} /> Aggiorna
          </Button>
        </div>
      </header>

      <div className="mb-4 font-mono text-xs text-muted-foreground">
        {showClosed
          ? <>Visualizzando <span className="font-semibold text-foreground">{closedTickets.length} ticket chiusi</span></>
          : <>Visualizzando <span className="font-semibold text-foreground">{openTickets.length} ticket aperti</span> · {closedTickets.length} chiusi nascosti</>}
      </div>

      {loading && <div className="text-muted-foreground">Caricamento…</div>}
      {!loading && visible.length === 0 && (
        <Card className="p-8 border-border/70 border-dashed rounded-lg text-center text-muted-foreground font-mono text-sm">
          — Nessun ticket {showClosed ? "chiuso" : "aperto"} —
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {visible.map((t) => {
          const um = URGENCY_META[t.urgency || "normal"];
          const sm = STATUS_META[t.status];
          const isClosed = t.status === "closed";
          return (
            <Card key={t.id} className={`p-5 border rounded-lg ${!isClosed && t.urgency === "high" ? "ring-2 ring-rose-500/40 border-rose-500/40" : "border-border/70"}`} data-testid={`ticket-${t.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap mb-2">
                    <Badge className={`${sm.cls} border font-mono text-[9px] uppercase`}>{sm.l}</Badge>
                    <Badge className={`${um.cls} border font-mono text-[9px] uppercase gap-1`}>
                      {t.urgency === "high" && <Warning size={10} weight="fill" />}
                      Urgenza: {um.l}
                    </Badge>
                    {t.contact_type && (
                      <Badge className="bg-secondary text-foreground border-border hover:bg-secondary font-mono text-[9px] uppercase gap-1">
                        {t.contact_type === "home" ? <><House size={10} weight="fill" /> Domicilio</> : <><Wrench size={10} weight="fill" /> Laboratorio</>}
                      </Badge>
                    )}
                    <Badge className="bg-secondary text-foreground border-border hover:bg-secondary font-mono text-[9px] uppercase">
                      {DEVICE_LABELS[t.device_type] || t.device_type}
                    </Badge>
                    {t.customer_id && (
                      <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30 font-mono text-[9px] uppercase gap-1">
                        <CheckCircle size={10} weight="fill" /> Cliente collegato
                      </Badge>
                    )}
                  </div>
                  <div className="font-semibold">{t.surname} {t.name}</div>
                  {t.company_name && (
                    <div className="text-xs text-muted-foreground font-mono">{t.company_name}</div>
                  )}
                  <div className="font-mono text-[11px] text-muted-foreground flex items-center gap-3 flex-wrap mt-1">
                    <a href={`tel:${t.phone}`} className="inline-flex items-center gap-1 hover:text-primary"><Phone size={11} /> {t.phone}</a>
                    <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1 hover:text-primary"><Envelope size={11} /> {t.email}</a>
                  </div>
                </div>
              </div>

              <div className="text-sm bg-secondary/40 border border-border/50 rounded-md p-3 mb-3 max-h-32 overflow-auto whitespace-pre-wrap">
                {t.description}
              </div>

              {t.notes && (
                <div className="text-xs text-muted-foreground border-l-2 border-primary/40 pl-2 py-1 mb-3">
                  <span className="font-mono text-[9px] uppercase tracking-widest">Note interne:</span> {t.notes}
                </div>
              )}

              <div className="font-mono text-[10px] text-muted-foreground mb-3 space-y-0.5">
                <div><Clock size={10} className="inline mr-1" /> Aperto: {formatDateTimeIT(t.opened_at || t.created_at)}</div>
                {t.closed_at && <div><CheckCircle size={10} className="inline mr-1" /> Chiuso: {formatDateTimeIT(t.closed_at)}</div>}
              </div>

              {!isClosed && (
                <div className="flex items-center gap-1 flex-wrap">
                  <Select value={t.urgency || "normal"} onValueChange={(v) => setUrgency(t, v)}>
                    <SelectTrigger className="h-7 w-28 text-xs" data-testid={`urgency-${t.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(URGENCY_META).map(([k, v]) => (
                        <SelectItem key={k} value={k} data-testid={`urgency-${t.id}-${k}`}>{v.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" onClick={() => composeEmail(t)} data-testid={`ticket-mail-${t.id}`} className="h-7 rounded-full gap-1 text-xs" title="Scrivi mail al cliente">
                    <PaperPlaneTilt size={12} /> Mail
                  </Button>
                  {!t.customer_id && (
                    <Button size="sm" variant="outline" onClick={() => createCustomer(t)} data-testid={`ticket-add-cust-${t.id}`} className="h-7 rounded-full gap-1 text-xs" title="Crea/collega cliente">
                      <UserPlus size={12} /> Cliente
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => openEdit(t)} data-testid={`ticket-edit-${t.id}`} className="h-7 rounded-full gap-1 text-xs" title="Modifica">
                    <PencilSimple size={12} />
                  </Button>
                  <div className="ml-auto flex items-center gap-1">
                    <Button size="sm" onClick={() => startClose(t)} data-testid={`ticket-close-${t.id}`} className="h-7 rounded-full gap-1 text-xs">
                      <CheckCircle size={12} weight="bold" /> Chiudi
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => removeTicket(t)} className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`ticket-delete-${t.id}`}>
                      <Trash size={13} />
                    </Button>
                  </div>
                </div>
              )}
              {isClosed && (
                <div className="flex items-center gap-1">
                  <Button size="sm" variant="outline" onClick={() => reopenTicket(t)} data-testid={`ticket-reopen-${t.id}`} className="h-7 rounded-full gap-1 text-xs">
                    <ArrowClockwise size={12} /> Riapri
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeTicket(t)} className="ml-auto h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10" data-testid={`ticket-delete-${t.id}`}>
                    <Trash size={13} />
                  </Button>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Edit dialog */}
      <Dialog open={editDialog.open} onOpenChange={(o) => !o && setEditDialog({ open: false, ticket: null, form: {} })}>
        <DialogContent className="max-w-lg" data-testid="ticket-edit-dialog">
          <DialogHeader>
            <DialogTitle>Modifica ticket</DialogTitle>
            <DialogDescription>Aggiorna i dati del ticket. Le modifiche non inviano email al cliente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="font-mono text-[10px] uppercase">Nome</Label><Input value={editDialog.form.name || ""} onChange={(e) => setEditDialog({...editDialog, form: {...editDialog.form, name: e.target.value}})} data-testid="edit-name" /></div>
              <div><Label className="font-mono text-[10px] uppercase">Cognome</Label><Input value={editDialog.form.surname || ""} onChange={(e) => setEditDialog({...editDialog, form: {...editDialog.form, surname: e.target.value}})} data-testid="edit-surname" /></div>
              <div><Label className="font-mono text-[10px] uppercase">Telefono</Label><Input value={editDialog.form.phone || ""} onChange={(e) => setEditDialog({...editDialog, form: {...editDialog.form, phone: e.target.value}})} data-testid="edit-phone" /></div>
              <div><Label className="font-mono text-[10px] uppercase">Email</Label><Input type="email" value={editDialog.form.email || ""} onChange={(e) => setEditDialog({...editDialog, form: {...editDialog.form, email: e.target.value}})} data-testid="edit-email" /></div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase">Nome azienda</Label>
              <Input value={editDialog.form.company_name || ""} onChange={(e) => setEditDialog({...editDialog, form: {...editDialog.form, company_name: e.target.value}})} placeholder="(opzionale)" data-testid="edit-company" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="font-mono text-[10px] uppercase">Urgenza</Label>
                <Select value={editDialog.form.urgency} onValueChange={(v) => setEditDialog({...editDialog, form: {...editDialog.form, urgency: v}})}>
                  <SelectTrigger data-testid="edit-urgency"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(URGENCY_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase">Tipo</Label>
                <Select value={editDialog.form.contact_type} onValueChange={(v) => setEditDialog({...editDialog, form: {...editDialog.form, contact_type: v}})}>
                  <SelectTrigger data-testid="edit-contact"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="workshop">Laboratorio</SelectItem>
                    <SelectItem value="home">Domicilio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase">Dispositivo</Label>
              <Select value={editDialog.form.device_type} onValueChange={(v) => setEditDialog({...editDialog, form: {...editDialog.form, device_type: v}})}>
                <SelectTrigger data-testid="edit-device"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(DEVICE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase">Descrizione</Label>
              <Textarea rows={4} value={editDialog.form.description || ""} onChange={(e) => setEditDialog({...editDialog, form: {...editDialog.form, description: e.target.value}})} data-testid="edit-description" />
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase">Note interne</Label>
              <Textarea rows={2} value={editDialog.form.notes || ""} onChange={(e) => setEditDialog({...editDialog, form: {...editDialog.form, notes: e.target.value}})} data-testid="edit-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialog({ open: false, ticket: null, form: {} })}>Annulla</Button>
            <Button onClick={saveEdit} disabled={savingEdit} data-testid="edit-save-btn" className="rounded-full gap-2">
              <FloppyDisk size={14} weight="bold" /> {savingEdit ? "Salvo…" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close dialog with movement question */}
      <Dialog open={closeDialog.open} onOpenChange={(o) => !o && setCloseDialog({ open: false, ticket: null, step: 1, addToMovements: null, price: "", note: "" })}>
        <DialogContent data-testid="ticket-close-dialog">
          <DialogHeader>
            <DialogTitle>Chiudere il ticket?</DialogTitle>
            <DialogDescription>
              {closeDialog.ticket?.surname} {closeDialog.ticket?.name} — {closeDialog.ticket?.description?.slice(0, 100)}
            </DialogDescription>
          </DialogHeader>
          {closeDialog.addToMovements === null && (
            <div>
              <p className="text-sm mb-4">Vuoi aggiungere questo ticket ai <strong>movimenti della dashboard</strong> con il prezzo dell'intervento?</p>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => setCloseDialog({ ...closeDialog, addToMovements: false })} data-testid="close-no-movement" className="h-20 flex flex-col gap-1">
                  <span className="font-semibold">No, chiudi e basta</span>
                  <span className="text-[10px] text-muted-foreground font-normal">Non finisce nei movimenti</span>
                </Button>
                <Button onClick={() => setCloseDialog({ ...closeDialog, addToMovements: true })} data-testid="close-with-movement" className="h-20 flex flex-col gap-1">
                  <span className="font-semibold">Sì, aggiungi</span>
                  <span className="text-[10px] font-normal opacity-80">Chiedimi il prezzo</span>
                </Button>
              </div>
            </div>
          )}
          {closeDialog.addToMovements === true && (
            <div className="space-y-3">
              {!closeDialog.ticket?.customer_id && (
                <div className="text-xs bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 rounded-md p-3">
                  <Warning size={14} className="inline mr-1" /> Devi prima <strong>creare/collegare il cliente</strong> al ticket. Chiudi questo dialogo, clicca "Cliente" sulla card, poi riprova.
                </div>
              )}
              <div>
                <Label className="font-mono text-[10px] uppercase">Prezzo intervento (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={closeDialog.price}
                  onChange={(e) => setCloseDialog({ ...closeDialog, price: e.target.value })}
                  data-testid="close-price"
                  className="mt-1.5 font-mono"
                  placeholder="Es. 45.00"
                />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase">Nota per il movimento (opzionale)</Label>
                <Input
                  value={closeDialog.note}
                  onChange={(e) => setCloseDialog({ ...closeDialog, note: e.target.value })}
                  data-testid="close-note"
                  className="mt-1.5"
                  placeholder="Es. Sostituzione HDD notebook"
                />
                <div className="text-[10px] text-muted-foreground mt-1 font-mono">Se lasci vuoto usa la descrizione del ticket.</div>
              </div>
            </div>
          )}
          <DialogFooter>
            {closeDialog.addToMovements !== null && (
              <Button variant="ghost" onClick={() => setCloseDialog({ ...closeDialog, addToMovements: null, price: "", note: "" })}>Indietro</Button>
            )}
            <Button variant="ghost" onClick={() => setCloseDialog({ open: false, ticket: null, step: 1, addToMovements: null, price: "", note: "" })}>Annulla</Button>
            {closeDialog.addToMovements !== null && (
              <Button
                onClick={submitClose}
                disabled={savingClose || (closeDialog.addToMovements && (!closeDialog.price || !closeDialog.ticket?.customer_id))}
                data-testid="close-confirm-btn"
                className="rounded-full gap-2"
              >
                <CheckCircle size={14} weight="bold" /> {savingClose ? "Chiudo…" : "Conferma chiusura"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
